import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import {
  CHANGE_BOOKMAKER,
  CHANGE_BOOKMAKER_EXTEND,
  CHANGE_ODDS_PERIOD,
} from "@/lib/redux/reducers/actionTypes";
import TiLeMatch from "@/components/TiLeMatch";
import LoadingInner from "@/components/Loading";
import NoData from "@/components/NoData";
import MatchLeagues from "@/components/MatchLeagues";
import KbvDateRange from "@/components/DateRange";
import KBVFilterV5 from "@/components/KBVFilterV5";
import { BANG_KEO_PAGE_LIMIT, BANG_KEO_PAGE_PLUGIN_V1_LIMIT } from "@/contants";
import Carousel from "@/components/Carousel";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import LoadMoreButton from "../LoadMoreButton";


dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";


const KbvBangKeo = ({ type = "all" }) => {
  const dispatch = useDispatch();
  const bookmarkerExtend = useSelector((state) => state.bookmarkerExtendReducer);
  const bookmarker = useSelector((state) => state.bookmakerReducer);
  const oddsPeriod = useSelector((state) => state.oddsPeriodReducer) ?? "full";

  const [firstLoad, setFirstLoad] = useState(true);
  const [isHotOnly, setIsHotOnly] = useState(false);
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);

  const today = dayjs().tz(TZ).format("YYYY-MM-DD");
  const [selectedDateValue, setSelectedDateValue] = useState('live');
  const { clearAllData } = useLiveData();

  const dateString =
    selectedDateValue === "live" ? today : selectedDateValue;
  const status =
    type === "ketqua" ? "end" : selectedDateValue === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_PLUGIN_V1_LIMIT,
    }),
    [dateString, status]
  );

  const {
      hotLeaguesWithMatches,
      leaguesWithMatches,
      loading,
      hasMore,
      hasMoreHot,
      isLoadingMore,
      handleLoadMore,
      handleLoadMoreHot,
      matches,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () => (isHotOnly ? hotLeaguesWithMatches : leaguesWithMatches),
    [isHotOnly, hotLeaguesWithMatches, leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    const idSet = new Set(selectedLeagueIds);
    return (baseLeaguesWithMatches || []).filter(({ league }) =>
      idSet.has(league.id),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  // live updates for all match IDs
  useLiveUpdates(matchIds);
  // clear match data live 
  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  useEffect(() => {
    if (firstLoad && selectedDateValue === 'live' && !loading && leaguesWithMatches.length <= 0) {
      setSelectedDateValue(today);
    }
  }, [firstLoad, loading, leaguesWithMatches.length, today, selectedDateValue]);

  const handleDateChange = (value) => {
    setSelectedDateValue(value);
    setFirstLoad(false);
  };

  return (
    <div className="mx-auto px-2 py-3 rounded-xl flex flex-col gap-2">
      <KbvDateRange
        value={selectedDateValue}
        onChange={handleDateChange}
      />
      <KBVFilterV5
        isHotOnly={isHotOnly}
        onHotChange={setIsHotOnly}
        selectedLeagueIds={selectedLeagueIds}
        onLeagueChange={setSelectedLeagueIds}
        bookmarkerExtend={bookmarkerExtend}
        onBookmakerExtendChange={(val) =>
          dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
        }
        bookmarker={bookmarker}
        onBookmakerChange={(val) =>
          dispatch({ type: CHANGE_BOOKMAKER, payload: val })
        }
        oddsPeriod={oddsPeriod}
        onOddsPeriodChange={(val) =>
          dispatch({ type: CHANGE_ODDS_PERIOD, payload: val })
        }
      />
      <Carousel matches={matches} />

      {loading && <LoadingInner />}
      {
        !loading && (
          <>
            <div className="space-y-2 rounded-lg">
              {
                filteredLeaguesWithMatches.length === 0 ? (
                  <NoData message="Không có trận đấu nào" />

                ) : (
                  filteredLeaguesWithMatches.map(({ league, matches: leagueMatches }) => (
                    <MatchLeagues key={league.id} league={league} matches={leagueMatches} render={(match) => <TiLeMatch key={match.id} match={match} />} />
                  ))
                )

              }
            </div>
            {!selectedLeagueIds?.length && (
              <LoadMoreButton
                isLoading={isLoadingMore}
                onClick={isHotOnly ? handleLoadMoreHot : handleLoadMore}
                loadingComponent={<LoadingInner />}
                hasMore={isHotOnly ? hasMoreHot : hasMore}
              />
            )}
          </>
        )
      }
    </div>
  );
};

export default KbvBangKeo;